import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { convertToJalali } from "../../../../utils/date";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { LocaleContext } from "../../../../routing/LangRouter";
import { t } from "i18next";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import BaseTabs from "../../../common/base-tabs/base-tabs";

//import view components
import IntFlightInformation from "../../flight/international/int-flight-information";
import FlightPdf from "./flight-pdf-file";
import OrderPassengers from "./order-passengers";
import BaggageRules from "../../flight/international/int-flight-baggage";
import FareRules from "../../flight/international/int-flight-fare";
import IntFlightPdf from "./int-flight-pdf-file";

//import images and icons
import { Add } from "iconsax-react";

//import styles
import "./order-details-modal.scss";

function FlightDetailsModalContent({ selectedOrderItem, handleClose }) {
    //general variables
    const [parent] = useAutoAnimate();
    const { locale } = useContext(LocaleContext);
    const isFlightInternational = selectedOrderItem?.flight?.is_international;

    //handle changing the tabs at the top of the modal
    const [activeTab, setActiveTab] = useState({ departure: true, return: false, fareRules: false, baggageRules: false });
    const handleTabChange = type => {
        setActiveTab({ [type]: true });
    };

    //list of tabs at the top of the flight order details modal
    const tabs = [
        {
            type: "departure",
            title: t("departureFlight"),
            activeTab: activeTab?.departure,
            disable: !isFlightInternational && !selectedOrderItem?.flight?.return_segments.length,
        },
        {
            type: "return",
            title: t("returnFlight"),
            activeTab: activeTab?.return,
            disable: !selectedOrderItem?.flight?.return_segments.length,
        },
        {
            type: "fareRules",
            title: t("visaRouteRules"),
            activeTab: activeTab?.fareRules,
            disable: !isFlightInternational,
        },
        {
            type: "baggageRules",
            title: t("baggageRules"),
            activeTab: activeTab?.baggageRules,
            disable: !isFlightInternational,
        },
    ];

    //handle click on the cancellation request button
    const [isCancellationRequest, setIsCancellationRequest] = useState(false);

    // Check if any ticket number is null or if the tickets array is empty, but only if isInternational is true
    const hasNullTicketNumber =
        selectedOrderItem?.flight?.segments?.some(segment =>
            segment?.passengers?.some(
                passenger => passenger.tag === "pr" && (passenger?.tickets?.[0]?.ticket_number === null || !passenger?.tickets.length),
            ),
        ) ||
        selectedOrderItem?.flight?.return_segments?.some(segment =>
            segment?.passengers?.some(
                passenger => passenger.tag === "pr" && (passenger?.tickets?.[0]?.ticket_number === null || !passenger?.tickets.length),
            ),
        );

    // States for managing baggage information and fare rules
    const [baggageInfo, setBaggageInfo] = useState(null);
    const [fareRules, setFareRules] = useState([]);

    return (
        <>
            <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
                <Grid container item xs={12} columnGap={1} alignItems="center">
                    <span className={`orders__label orders__label--${selectedOrderItem?.get_status?.en}`}>{selectedOrderItem?.get_status?.fa}</span>
                    <span className="orders__label orders__label--date">{t("General.Order Date")}: {convertToJalali(selectedOrderItem?.create_date)}</span>
                    <span className="orders__label orders__label--tracking-code">{t("trackingCode")}: {selectedOrderItem?.tracking_code}</span>
                </Grid>
                <Add className="close" size="32" color="var(--teal-color-18)" onClick={handleClose} cursor="pointer" />
            </DialogTitle>

            <DialogContent dividers={true}>
                <Grid container item xs={isFlightInternational ? 10 : 6} display={{ xs: "inline", md: "flex" }} mb={3}>
                    <BaseTabs handleTabChange={handleTabChange} list={tabs} style={{ top: "-1rem" }} />
                </Grid>
                {activeTab.departure || activeTab.return ? (
                    <Grid container item xs={12} rowGap={3}>
                        <Grid container item xs={12} justifyContent="space-between">
                            <span className="order-details-modal__title">{t("flightInformation")}</span>
                            {!isFlightInternational && (
                                <Grid container item xs={3} md={1.5}>
                                    <Link className="order-details-modal__cancel-rules-link" target="_blank" to={`/${locale}/refund-rules`}>
                                       {t("refundRules")} 
                                    </Link>
                                </Grid>
                            )}
                        </Grid>
                        {selectedOrderItem?.flight?.[activeTab.departure ? "segments" : "return_segments"]?.map((segment, index) => {
                            return (
                                <Grid key={Math.random()} container item xs={12} justifyContent="flex-end" rowGap={{ xs: 2, md: 5 }}>
                                    <IntFlightInformation
                                        intFlightDetails={selectedOrderItem?.flight}
                                        segment={segment}
                                        index={index}
                                        type="orders"
                                    />
                                </Grid>
                            );
                        })}

                        <OrderPassengers
                            orderItem={selectedOrderItem?.flight?.[activeTab.departure ? "segments" : "return_segments"][0]}
                            isCancellationRequest={isCancellationRequest}
                        />
                    </Grid>
                ) : null}

                {activeTab?.fareRules && (
                    <FareRules
                        fareRules={fareRules}
                        setFareRules={setFareRules}
                        fareSourceCode={selectedOrderItem?.flight?.fare_source_code}
                        uniqueId={selectedOrderItem?.flight?.unique_id}
                    />
                )}
                {activeTab?.baggageRules && (
                    <BaggageRules
                        baggageInfo={baggageInfo}
                        setBaggageInfo={setBaggageInfo}
                        fareSourceCode={selectedOrderItem.flight.fare_source_code}
                        uniqueId={selectedOrderItem?.flight.unique_id}
                    />
                )}
            </DialogContent>

            <DialogActions ref={parent}>
                <Grid className="order-details-modal__download" container item md={2}>
                    <PDFDownloadLink
                        document={isFlightInternational ? <IntFlightPdf orderItem={selectedOrderItem} /> : <FlightPdf orderItem={selectedOrderItem} />}
                        fileName={`ticket_id_.pdf`}
                    >
                        {({ blob, url, loading, error }) => (
                            <BaseButton
                                text={`${t("download")} PDF ${hasNullTicketNumber ? `(${t("General.Being Issued")})` : ""}`}
                                color="secondary"
                                radius="radius-10"
                                size="medium"
                                loading={loading}
                                disabled={hasNullTicketNumber}
                            />
                        )}
                    </PDFDownloadLink>
                </Grid>

                {isCancellationRequest ? (
                    <>
                        <Grid container item md={2}>
                            <BaseButton
                                text={t("General.Cancellation")}
                                color="outline-red"
                                size="medium"
                                radius="radius-10"
                                handleClick={() => setIsCancellationRequest(false)}
                            />
                        </Grid>
                        <Grid container item md={2}>
                            <BaseButton text={t("confirmation")} color="red" size="medium" radius="radius-10" />
                        </Grid>
                    </>
                ) : (
                    <Grid container item md={2}>
                        <BaseButton
                            text={t("General.Cancellation Request")}
                            color="red"
                            size="medium"
                            radius="radius-10"
                            handleClick={() => setIsCancellationRequest(true)}
                        />
                    </Grid>
                )}
            </DialogActions>
        </>
    );
}

export default FlightDetailsModalContent;
